import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// Styles for the shared day × week intensity heat grid — lifted verbatim
// from apps/mobile previewStyles.ts (the v3.2.0 Home preview look) so the
// Home re-mount renders identically. Row = one ≥44pt tap target;
// selection is per-day, never per-cell.
export function makeGridStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    gridHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      marginBottom: s.s1,
    },
    gridDayCol: { width: 28 },
    gridWeekHead: {
      flex: 1,
      textAlign: "center",
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontVariant: ["tabular-nums"],
    },
    // Baseline rows carry an invisible border with the same metrics as
    // the selected ring, so selecting never shifts the layout.
    gridRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      minHeight: 44,
      borderWidth: 2,
      borderColor: "transparent",
      borderRadius: r.r3,
      paddingHorizontal: s.s1,
    },
    // The selected day reads as a frame, not a colour shift: brand ring +
    // surface tint (values are visible on every row, so the ring is the
    // only selection signal it needs — and it is never colour-alone, the
    // day number also flips to brand).
    gridRowSelected: {
      borderColor: p.brand500,
      backgroundColor: p.surface,
    },
    gridRowPressed: { opacity: 0.6 },
    gridDayNum: {
      width: 28,
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    gridDayNumSelected: { color: p.brand500 },
    gridCells: {
      flex: 1,
      flexDirection: "row",
      gap: s.s1,
    },
    gridCell: {
      flex: 1,
      height: 26,
      borderRadius: r.r2,
      alignItems: "center",
      justifyContent: "center",
    },
    // Band glyph (W/M/H/X) in the cell's top-left corner — the non-colour cue
    // so the band never rides on fill colour alone, so it must READ: `xs` (11)
    // matching the legibility floor bumped across this grid, full opacity (it's
    // the safety cue, not decoration), colour set inline to the band's `on` ink.
    gridCellBand: {
      position: "absolute",
      top: 1,
      left: 3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
    },
    // Training prescribed but no banded intensity (kind none/history/kg):
    // outlined instead of filled, the set count as the cell text — a
    // "free" day must never read as a rest day.
    gridCellFree: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: p.border,
    },
    gridCellNum: {
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    gridCellFreeNum: { color: p.ink2 },
    gridHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      marginTop: s.s1,
    },
    // Spacing only — the swatch/glyph/label layout lives in <ScaleLegend> now.
    legendRow: { marginTop: s.s3 },
  });
}
